import type { GameConfig } from '../shared/config';
import { MISSED_WORD_LIMIT, compareRoundPlayers } from '../shared/results';
import type { RoundPlayerResult, RoundResults, WordView } from '../shared/results';

export interface Submission {
  word: string;
  points: number;
  acceptedAt: number;
}

export interface RoundParticipant {
  id: string;
  name: string;
  avatar: string;
  totalBefore: number;
  submissions: ReadonlyMap<string, Submission>;
  bot?: true;
}

function isPrime(value: number): boolean {
  for (let divisor = 2; divisor * divisor <= value; divisor += 1) {
    if (value % divisor === 0) return false;
  }
  return value >= 2;
}

function nextPrime(value: number): number {
  let candidate = value + 1;
  while (!isPrime(candidate)) candidate += 1;
  return candidate;
}

export function pointsForWord(word: string): number {
  const length = word.length;
  if (length <= 3) return 100;
  if (length === 4) return 200;
  if (length === 5) return 300;
  if (length === 6) return 500;

  let multiplier = 11;
  for (let currentLength = 7; currentLength < length; currentLength += 1) {
    multiplier = nextPrime(multiplier);
  }
  return multiplier * 100;
}

export function isWithinSubmissionDeadline(
  now: number,
  startAt: number,
  endAt: number,
  graceMs: number,
): boolean {
  return Number.isFinite(now) && now >= startAt && now <= endAt + graceMs;
}

function duplicateCounts(submissions: Iterable<RoundParticipant>): Map<string, number> {
  const counts = new Map<string, number>();
  for (const participant of submissions) {
    for (const word of participant.submissions.keys()) counts.set(word, (counts.get(word) ?? 0) + 1);
  }
  return counts;
}

export function scoreRound(
  participants: Iterable<RoundParticipant>,
  allWords: ReadonlySet<string>,
  config: GameConfig,
  round: number,
  roundId: string,
  board: string[],
  startedAt: number,
  endedAt: number,
): RoundResults {
  const list = [...participants];
  const frequency = duplicateCounts(list);
  const players: RoundPlayerResult[] = list.map((participant) => {
    let score = 0;
    const words: WordView[] = [...participant.submissions.values()]
      .sort((a, b) => b.points - a.points || a.word.localeCompare(b.word))
      .map((submission) => {
        const duplicate = frequency.get(submission.word) !== 1;
        const points =
          config.scoringMode === 'classic' && !duplicate ? submission.points * 1.5 : submission.points;
        score += points;
        return { w: submission.word, pts: points, ...(duplicate ? { dup: true } : {}) };
      });
    return {
      id: participant.id,
      name: participant.name,
      avatar: participant.avatar,
      score,
      total: participant.totalBefore + score,
      words,
      ...(participant.bot ? { bot: true as const } : {}),
    };
  });
  players.sort(compareRoundPlayers);
  const missed = [...allWords]
    .filter((word) => !frequency.has(word))
    .sort((a, b) => b.length - a.length || a.localeCompare(b))
    .slice(0, MISSED_WORD_LIMIT)
    .map((word) => ({ w: word, pts: pointsForWord(word) }));
  const found = frequency.size;
  return {
    round,
    roundId,
    config: { ...config },
    players,
    missed,
    possible: allWords.size,
    found,
    board: [...board],
    startedAt,
    endedAt,
  };
}
